import { useState } from "react";
import useDataset from "datasets/hooks/useDataset";
import { usePreviewMode } from "datasets/hooks/usePreviewMode";
import Dataset from "datasets/types/interfaces/dataset";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { apiMutate } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

interface EnablePreviewModeOptionProps {
  dataset: Pick<Dataset, "id" | "title" | "previewEnabled">;
  // Owned by the parent so other entry points (the locked Preview access level)
  // can open the same confirmation.
  confirmOpen: boolean;
  onConfirmOpenChange: (open: boolean) => void;
  // Runs once the API has enabled preview, before the confirmation closes.
  onEnabled?: () => void;
}

/** Share-modal hint with a link that turns on Preview access for a dataset. Renders nothing when the feature is off or already enabled. */
export const EnablePreviewModeOption = ({
  dataset,
  confirmOpen,
  onConfirmOpenChange,
  onEnabled,
}: EnablePreviewModeOptionProps) => {
  const previewFeatureEnabled = usePreviewMode();
  const { mutate } = useDataset(dataset.id);
  const { showSuccess, showError } = useToast();
  const [isEnabling, setIsEnabling] = useState(false);

  if (!previewFeatureEnabled || dataset.previewEnabled) {
    return null;
  }

  const handleEnable = async () => {
    setIsEnabling(true);
    try {
      await apiMutate(
        `${VITE_CATALOG_API}/datasets/${dataset.id}/preview/enable`,
        "POST",
        undefined,
        { expectsText: true }
      );
      mutate((current) => (current ? { ...current, previewEnabled: true } : current), {
        revalidate: true,
      });
      showSuccess("Preview mode enabled");
      onEnabled?.();
      onConfirmOpenChange(false);
    } catch {
      showError("Unable to enable preview mode");
    } finally {
      setIsEnabling(false);
    }
  };

  return (
    <>
      <p className="mt-1.5 text-xs text-muted-foreground">
        <button
          type="button"
          onClick={() => onConfirmOpenChange(true)}
          className="cursor-pointer font-medium text-primary underline-offset-2 hover:underline"
        >
          Enable Preview Mode
        </button>{" "}
        to share with Preview access, where users see metadata only.
      </p>

      <AlertDialog open={confirmOpen} onOpenChange={onConfirmOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable Preview Mode</AlertDialogTitle>
            <AlertDialogDescription>
              You are enabling preview mode for &ldquo;{dataset.title}&rdquo;. This allows the
              dataset to be shared with preview access, where users see metadata only, not
              underlying data. This setting cannot be disabled without administrator assistance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isEnabling}>Cancel</AlertDialogCancel>
            <Button onClick={handleEnable} disabled={isEnabling}>
              {isEnabling ? "Enabling…" : "Enable"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
